import { gql, useQuery } from "@apollo/client";
import { EyeOutlined, ReloadOutlined } from "@ant-design/icons";
import { Button, DatePicker, Flex, Input, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import RobinPage from "../components/RobinPage";

const { RangePicker } = DatePicker;

const ADMIN_GET_UNASSIGNED_ORDERS = gql`
  query AdminGetUnassignedOrders(
    $id: Int
    $storeId: Int
    $createdAfter: DateTime
    $createdBefore: DateTime
    $pagination: PaginationInput
  ) {
    adminGetUnassignedOrders(
      filter: {
        id: $id
        storeId: $storeId
        createdAfter: $createdAfter
        createdBefore: $createdBefore
      }
      pagination: $pagination
    ) {
      total
      hasMore
      limit
      offset
      orders {
        id
        status
        storeName
        scheduledAt
        createdAt
        userPhoneNumber
        itemsCount
      }
    }
  }
`;

const GET_ALL_STORES = gql`
  query GetAllStores {
    getAllStores {
      id
      name
      nameAr
    }
  }
`;

type UnassignedOrder = {
  id?: number | null;
  status?: string | null;
  storeName?: string | null;
  scheduledAt?: string | null;
  createdAt?: string | null;
  userPhoneNumber?: string | null;
  itemsCount?: number | null;
} | null;

type AdminGetUnassignedOrdersResponse = {
  adminGetUnassignedOrders?: {
    total?: number | null;
    hasMore?: boolean | null;
    limit?: number | null;
    offset?: number | null;
    orders?: UnassignedOrder[] | null;
  } | null;
};

type Store = {
  id?: number | null;
  name?: string | null;
  nameAr?: string | null;
} | null;

type GetAllStoresResponse = {
  getAllStores?: Store[] | null;
};

function UnassignedOrdersList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [orderIdInput, setOrderIdInput] = useState("");
  const [storeIdInput, setStoreIdInput] = useState<number | undefined>();
  const [dateRangeInput, setDateRangeInput] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null);

  const [orderIdFilter, setOrderIdFilter] = useState<number | undefined>();
  const [storeIdFilter, setStoreIdFilter] = useState<number | undefined>();
  const [dateRangeFilter, setDateRangeFilter] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const { data: storesData } = useQuery<GetAllStoresResponse>(GET_ALL_STORES);

  const { data, loading, error, refetch } =
    useQuery<AdminGetUnassignedOrdersResponse>(ADMIN_GET_UNASSIGNED_ORDERS, {
      variables: {
        id: orderIdFilter,
        storeId: storeIdFilter,
        createdAfter: dateRangeFilter?.[0]?.startOf("day").toISOString(),
        createdBefore: dateRangeFilter?.[1]?.endOf("day").toISOString(),
        pagination: {
          limit: pageSize,
          offset: (currentPage - 1) * pageSize,
        },
      },
      notifyOnNetworkStatusChange: true,
    });

  const handleApplyFilters = () => {
    const parsedOrderId = orderIdInput.trim()
      ? Number(orderIdInput.trim())
      : undefined;

    setOrderIdFilter(Number.isFinite(parsedOrderId) ? parsedOrderId : undefined);
    setStoreIdFilter(storeIdInput);
    setDateRangeFilter(dateRangeInput);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setOrderIdInput("");
    setStoreIdInput(undefined);
    setDateRangeInput(null);
    setOrderIdFilter(undefined);
    setStoreIdFilter(undefined);
    setDateRangeFilter(null);
    setCurrentPage(1);
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return "-";

    const parsedValue = dayjs(value);
    return parsedValue.isValid() ? parsedValue.format("DD/MM/YYYY HH:mm") : value;
  };

  const getStatusColor = (status?: string | null) => {
    switch (status?.toUpperCase()) {
      case "PENDING":
        return "warning";
      case "ASSIGNED":
        return "processing";
      case "CANCELLED":
        return "error";
      case "COMPLETED":
        return "success";
      default:
        return "default";
    }
  };

  const storeOptions = useMemo(
    () =>
      (storesData?.getAllStores ?? [])
        .filter(
          (store): store is NonNullable<Store> =>
            store !== null && store !== undefined && store.id !== null,
        )
        .map((store) => ({
          value: Number(store.id),
          label: `${store.id} - ${
            i18n.language?.startsWith("ar")
              ? store.nameAr || store.name || "-"
              : store.name || store.nameAr || "-"
          }`,
        })),
    [i18n.language, storesData?.getAllStores],
  );

  const orders = data?.adminGetUnassignedOrders?.orders ?? [];
  const total = data?.adminGetUnassignedOrders?.total ?? 0;

  const dataSource = orders.map((order, index) => ({
    key: order?.id ?? index,
    id: order?.id ?? "-",
    status: order?.status ?? "-",
    storeName: order?.storeName ?? "-",
    scheduledAt: formatDateTime(order?.scheduledAt),
    createdAt: formatDateTime(order?.createdAt),
    userPhoneNumber: order?.userPhoneNumber ?? "-",
    itemsCount: order?.itemsCount ?? 0,
    rawOrderId: order?.id ?? null,
  }));

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 100,
    },
    {
      title: t("label.status"),
      dataIndex: "status",
      key: "status",
      render: (status: string) =>
        status === "-" ? (
          "-"
        ) : (
          <Tag color={getStatusColor(status)}>{status}</Tag>
        ),
    },
    {
      title: t("label.store.name"),
      dataIndex: "storeName",
      key: "storeName",
    },
    {
      title: t("label.order.scheduled_at"),
      dataIndex: "scheduledAt",
      key: "scheduledAt",
    },
    {
      title: t("label.created_at"),
      dataIndex: "createdAt",
      key: "createdAt",
    },
    {
      title: t("label.order.user_phone_number"),
      dataIndex: "userPhoneNumber",
      key: "userPhoneNumber",
    },
    {
      title: t("label.order.items_count"),
      dataIndex: "itemsCount",
      key: "itemsCount",
      width: 120,
    },
    {
      title: t("label.actions"),
      key: "actions",
      width: 100,
      align: "center" as const,
      render: (_: unknown, record: { rawOrderId: number | null }) => (
        <Tooltip title={t("button.view")}>
          <Button
            type="primary"
            shape="circle"
            icon={<EyeOutlined />}
            onClick={() => {
              if (record.rawOrderId) {
                navigate(`/orders/unassigned/${record.rawOrderId}`);
              }
            }}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <RobinPage
      title={t("label.menu.unassigned_orders")}
      actions={
        <Space size="middle">
          <Button
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            disabled={loading}
          >
            {t("button.refresh")}
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Flex gap="middle" wrap="wrap" style={{ width: "100%" }}>
          <Input
            allowClear
            value={orderIdInput}
            placeholder={t("label.order.id")}
            onChange={(event) => setOrderIdInput(event.target.value)}
            onPressEnter={handleApplyFilters}
            style={{ flex: "1 1 calc(25% - 8px)", minWidth: 180 }}
          />
          <Select
            showSearch
            allowClear
            value={storeIdInput}
            placeholder={t("label.store.select")}
            options={storeOptions}
            onChange={(value) => setStoreIdInput(value)}
            filterOption={(input, option) =>
              String(option?.label ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            style={{ flex: "1 1 calc(25% - 8px)", minWidth: 220 }}
          />
          <RangePicker
            value={dateRangeInput}
            onChange={(dates) =>
              setDateRangeInput(dates as [Dayjs | null, Dayjs | null] | null)
            }
            format="DD/MM/YYYY"
            style={{ flex: "1 1 calc(25% - 8px)", minWidth: 260 }}
          />
          <Space.Compact style={{ flex: "1 1 calc(25% - 8px)", minWidth: 220 }}>
            <Button
              type="primary"
              onClick={handleApplyFilters}
              style={{ flex: 1 }}
            >
              {t("button.search")}
            </Button>
            <Button onClick={handleResetFilters} style={{ flex: 1 }}>
              {t("button.reset_filters")}
            </Button>
          </Space.Compact>
        </Flex>
      </Space>

      {error ? (
        <Typography.Text type="danger">
          {error.message || t("error.failed_to_load")}
        </Typography.Text>
      ) : null}

      <Table
        loading={loading}
        dataSource={dataSource}
        columns={columns}
        pagination={{
          current: currentPage,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (itemsTotal) => t("label.total_items", { total: itemsTotal }),
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
        }}
      />
    </RobinPage>
  );
}

export default UnassignedOrdersList;
